import { useState, useCallback } from 'react'
import { type Image } from '@/api/accommodation/types'
import { Badge } from '@/components/ui/badge'
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import Discount from '@/components/money/discount'
import { MobileGallerySheet } from './MobileGallerySheet'
import { getAccommodationTransitionName } from '@/lib/viewTransitions'

interface ImageGalleryMobileProps {
  images: Image[] | undefined
  typeName?: string
  discount?: {
    minPrice: number
    maxPrice: number
  }
  unitId: number
}

export function ImageGalleryMobile({ images, typeName, discount, unitId }: ImageGalleryMobileProps) {
  const carouselImages = images || []
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const transitionName = getAccommodationTransitionName(unitId)

  const onApiChange = useCallback((api: CarouselApi) => {
    if (!api) return
    setCurrentIndex(api.selectedScrollSnap())
    api.on('select', () => {
      setCurrentIndex(api.selectedScrollSnap())
    })
  }, [])

  return (
    <>
      <div className="-mx-4" style={{ viewTransitionName: transitionName }}>
        {carouselImages.length > 0 ? (
          <div className="relative">
            <Carousel setApi={onApiChange} opts={{ loop: true }}>
              <CarouselContent className="ml-0">
                {carouselImages.map((image, index) => (
                  <CarouselItem key={index} className="pl-0">
                    <button
                      type="button"
                      aria-label="Otevřít galerii"
                      className="block w-full cursor-pointer"
                      onClick={() => setIsGalleryOpen(true)}
                    >
                      <img
                        src={image.url.thumbnail}
                        className="h-90 w-full object-cover"
                        alt={image.description || undefined}
                      />
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
            {/* Image counter - bottom right */}
            <Badge variant="secondary" className="absolute right-3 bottom-4 bg-black text-white">
              {currentIndex + 1}/{carouselImages.length}
            </Badge>
            {/* Badges - bottom left */}
            {(discount || typeName) && (
              <div className="absolute bottom-4 left-3 flex max-w-[calc(100%-64px)] flex-wrap items-center gap-2">
                {discount && discount.maxPrice > discount.minPrice && (
                  <Discount minPrice={discount.minPrice} maxPrice={discount.maxPrice} />
                )}
                {typeName && <Badge variant="secondary">{typeName}</Badge>}
              </div>
            )}
          </div>
        ) : (
          <ImagePlaceholder className="h-72 w-full" />
        )}
      </div>

      {/* Mobile gallery sheet */}
      {images && images.length > 0 && (
        <MobileGallerySheet images={images} isOpen={isGalleryOpen} onOpenChange={setIsGalleryOpen} />
      )}
    </>
  )
}
